import { OrganizationLocationType } from '@today/types'
import { Badge, Button, Dialog } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { ProductConfirmDialogView } from '../product/product-confirm-dialog.view'
import { ErrorText, SelectInput, TextInput } from '../product/product-fields'
import type { OrgConfirmState, OrgEditorState, OrgLocationFormView, OrgLocationRowView, Translate } from './org-types'

type LocationTextField = Exclude<keyof OrgLocationFormView, 'locationType'>
const TEXT_FIELDS: LocationTextField[] = [
	'addressLine1',
	'addressLine2',
	'postalCode',
	'city',
	'province',
	'region',
	'countryCode',
	'latitude',
	'longitude',
	'phone',
	'email',
]

/**
 * Props for {@link OrgLocationsView}.
 *
 * @property onAdd / onEdit / onDelete - Present only with `organization_unit.write`.
 */
export interface OrgLocationsViewProps {
	t: Translate
	state: 'loading' | 'error' | 'ready'
	rows: OrgLocationRowView[]
	editor: OrgEditorState<OrgLocationFormView> | null
	deleting: OrgConfirmState | null
	isBusy: boolean
	onRetry: () => void
	onEditorChange: (field: keyof OrgLocationFormView, value: string) => void
	onEditorSubmit: () => void
	onEditorClose: () => void
	onConfirmDelete: () => void
	onCancelDelete: () => void
	onAdd?: () => void
	onEdit?: (id: string) => void
	onDelete?: (id: string) => void
}

function EditorDialog({ props }: { props: OrgLocationsViewProps }): ReactElement | null {
	const { t, editor } = props
	if (!editor) return null
	const title = t(editor.mode === 'create' ? 'locations.dialogNew' : 'locations.dialogEdit')
	return (
		<Dialog isOpen onOpenChange={(open) => (open ? undefined : props.onEditorClose())} title={title}>
			<form
				aria-label={title}
				className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto"
				onSubmit={(e) => {
					e.preventDefault()
					props.onEditorSubmit()
				}}
			>
				<SelectInput
					label={t('locations.fields.locationType')}
					value={editor.values.locationType}
					emptyLabel=""
					options={Object.values(OrganizationLocationType).map((v) => ({ id: v, label: t(`locationType.${v}`) }))}
					error={editor.errors.locationType}
					onChange={(v) => props.onEditorChange('locationType', v)}
				/>
				{TEXT_FIELDS.map((name) => (
					<TextInput
						key={name}
						label={t(`locations.fields.${name}`)}
						value={editor.values[name]}
						error={editor.errors[name]}
						onChange={(v) => props.onEditorChange(name, v)}
					/>
				))}
				<ErrorText message={editor.formError} />
				<div className="flex gap-2">
					<Button type="submit" isDisabled={props.isBusy}>
						{t('locations.save')}
					</Button>
					<Button variant="muted" onPress={props.onEditorClose}>
						{t('locations.cancel')}
					</Button>
				</div>
			</form>
		</Dialog>
	)
}

function LocationRow({ props, row }: { props: OrgLocationsViewProps; row: OrgLocationRowView }): ReactElement {
	const { t } = props
	return (
		<li className="flex items-center gap-2 text-ds-sm">
			<Badge size="sm" variant={row.locationType === OrganizationLocationType.PRIMARY ? 'primary' : 'muted'}>
				{t(`locationType.${row.locationType}`)}
			</Badge>
			<span>{row.addressLine1}</span>
			<span>{row.postalCode ?? ''}</span>
			<span>{row.city}</span>
			<span>{row.countryCode}</span>
			<span className="ml-auto flex gap-2">
				{props.onEdit ? (
					<Button size="sm" variant="muted" onPress={() => props.onEdit?.(row.id)}>
						{t('locations.edit')}
					</Button>
				) : null}
				{props.onDelete ? (
					<Button size="sm" variant="danger" onPress={() => props.onDelete?.(row.id)}>
						{t('locations.delete')}
					</Button>
				) : null}
			</span>
		</li>
	)
}

function Body({ props }: { props: OrgLocationsViewProps }): ReactElement {
	const { t } = props
	if (props.state === 'loading') return <p aria-live="polite">{t('tree.loading')}</p>
	if (props.state === 'error') {
		return (
			<div role="alert" className="flex items-center gap-2 text-ds-sm">
				{t('locations.error')}
				<Button size="sm" variant="muted" onPress={props.onRetry}>
					{t('tree.retry')}
				</Button>
			</div>
		)
	}
	if (props.rows.length === 0) return <p className="text-ds-sm text-ds-text-muted">{t('locations.empty')}</p>
	return (
		<ul className="flex flex-col gap-2">
			{props.rows.map((row) => (
				<LocationRow key={row.id} props={props} row={row} />
			))}
		</ul>
	)
}

/** A unit's locations: list, add/edit dialog and guarded delete (organism). */
export function OrgLocationsView(props: OrgLocationsViewProps): ReactElement {
	const { t } = props
	return (
		<section aria-label={t('locations.title')} className="flex flex-col gap-2">
			<header className="flex items-center justify-between">
				<h3 className="text-ds-base font-semibold">{t('locations.title')}</h3>
				{props.onAdd ? (
					<Button size="sm" onPress={props.onAdd}>
						{t('locations.add')}
					</Button>
				) : null}
			</header>
			<Body props={props} />
			<EditorDialog props={props} />
			<ProductConfirmDialogView
				t={t}
				isOpen={props.deleting !== null}
				title={t('locations.deleteTitle')}
				body={t('locations.deleteBody', { address: props.deleting?.label ?? '' })}
				confirmLabel={t('locations.confirmDelete')}
				cancelLabel={t('locations.cancel')}
				danger
				error={props.deleting?.error}
				onConfirm={props.onConfirmDelete}
				onCancel={props.onCancelDelete}
			/>
		</section>
	)
}
